import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Animation sequence
 *
 * [initial]        EMANUEL          PISTARÀ_
 * [phase1  0-42%]  EMANUEL          PISTAR_  →  P_   (delete right to left)
 * [merge  42-50%]  EMANUELP_P                   (line1 fades out, combined in line0)
 * [phase2 50-82%]  EMANUELL_P  →  EP            (delete inner chars, cursor before P)
 * [phase3 82-100%] fly-element (body-level) flies from line0's rect to header slot
 *
 * IMPORTANT: Phase 3 uses a fly element appended to document.body — never to the
 * GSAP-pinned hero — so position:fixed works relative to the real viewport.
 */

const SCROLL_VH = 1.3;
const P1_END    = 0.42;
const MERGE_END = 0.50;
const P2_END    = 0.82;
const CURSOR_CH = '_';
const NBSP      = '\u00a0';

// ---------------------------------------------------------------------------
// DOM / text helpers
// ---------------------------------------------------------------------------

function setLineText(lineEl, text) {
  lineEl.querySelectorAll('[data-name-text]').forEach((el) => {
    el.textContent = text;
  });
}

function resetLine(line) {
  gsap.set(line, { clearProps: 'all' });
  line.style.cssText = '';
}

function p1Text(word, t) {
  const steps = word.length - 1;
  const del   = Math.min(steps, Math.floor(t * (steps + 1)));
  return word.slice(0, word.length - del);
}

function p2Text(combined, t) {
  const inner = combined.slice(1, -1);
  const steps = inner.length;
  const del   = Math.min(steps, Math.floor(t * (steps + 1)));
  return combined[0] + inner.slice(0, inner.length - del) + combined[combined.length - 1];
}

function withCursor(text, isPhase1, cursorOn) {
  const ch = cursorOn ? CURSOR_CH : NBSP;
  if (text.length <= 2) return text;
  if (isPhase1) return text + ch;
  return text.slice(0, -1) + ch + text.slice(-1);
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function initHeroNameScroll({ heroSection, nameContainer, nameLines, headerSlot, names, onMonitorProgress }) {
  if (!heroSection || !nameContainer || !headerSlot || nameLines.length < 2) {
    return () => {};
  }

  const line0    = nameLines[0];
  const line1    = nameLines[1];

  const fullName0 = names[0].toUpperCase();
  const fullName1 = names[1].toUpperCase();
  const letter0   = fullName0[0];
  const letter1   = fullName1[0];
  const combined  = fullName0 + letter1;

  let startRects = captureNaturalRects();

  function captureNaturalRects() {
    resetLine(line0);
    resetLine(line1);
    setLineText(line0, fullName0);
    setLineText(line1, fullName1);
    return [line0.getBoundingClientRect(), line1.getBoundingClientRect()];
  }

  if (nameContainer) {
    const totalH = startRects.reduce((acc, r, i) => acc + r.height + (i ? 8 : 0), 0);
    nameContainer.style.minHeight = `${totalH}px`;
  }

  // ── Fly element ──────────────────────────────────────────────────────────
  // Lives on document.body so position:fixed is never trapped inside a
  // GSAP will-change/transform ancestor.
  let flyEl       = null;
  let phase3Rect  = null;

  function spawnFly(rect) {
    destroyFly();
    const srcSpan = line0.querySelector('[data-name-text]:not([data-gl])') || line0.querySelector('[data-name-text]');
    const cs      = srcSpan ? window.getComputedStyle(srcSpan) : {};
    const el      = document.createElement('div');
    el.textContent = letter0 + letter1;
    Object.assign(el.style, {
      position:           'fixed',
      left:               '0',
      top:                '0',
      margin:             '0',
      padding:            '0',
      pointerEvents:      'none',
      zIndex:             '99999',
      whiteSpace:         'nowrap',
      fontFamily:         '"Doto", sans-serif',
      fontOpticalSizing:  'auto',
      fontVariationSettings: '"ROND" 0',
      fontSize:           cs.fontSize  || 'clamp(3.8rem, 10.5vw, 10rem)',
      fontWeight:         '700',
      lineHeight:         '0.9',
      letterSpacing:      '-0.02em',
      color:              cs.color     || 'var(--text-primary)',
      textTransform:      'uppercase',
      transformOrigin:    'left top',
      transform:          `translate(${rect.left}px, ${rect.top}px)`,
    });
    document.body.appendChild(el);
    flyEl = el;
    return el;
  }

  function destroyFly() {
    if (flyEl) {
      const el = flyEl;
      flyEl = null;
      // Rimozione deferred: evita conflitti con la commit phase di React
      requestAnimationFrame(() => { try { el.remove(); } catch (_) {} });
    }
  }

  // ── State ─────────────────────────────────────────────────────────────────
  let cursorOn = true;
  let lastP    = 0;

  // ── apply(p) ─────────────────────────────────────────────────────────────
  function apply(p) {
    p = gsap.utils.clamp(0, 1, p);
    lastP = p;

    // Notifica il monitor del progresso (P2_END = 0.82 → 100%)
    if (onMonitorProgress) {
      onMonitorProgress(Math.max(0, Math.min(1, (p - P2_END) / (1 - P2_END))));
    }

    // ── Reset ──────────────────────────────────────────────────────────────
    if (p <= 0) {
      resetLine(line0); resetLine(line1);
      setLineText(line0, fullName0);
      setLineText(line1, withCursor(fullName1, true, cursorOn));
      destroyFly(); phase3Rect = null;
      headerSlot.dataset.morphed = 'false';
      return;
    }

    // ── Phase 1: delete PISTARÀ → P ───────────────────────────────────────
    if (p < P1_END) {
      const txt = p1Text(fullName1, p / P1_END);
      resetLine(line0); resetLine(line1);
      setLineText(line0, fullName0);
      setLineText(line1, withCursor(txt, true, cursorOn));
      gsap.set([line0, line1], { zIndex: 9999, position: 'relative' });
      destroyFly(); phase3Rect = null;
      headerSlot.dataset.morphed = 'false';
      return;
    }

    // ── Merge: P fades from line1, combined appears on line0 ──────────────
    if (p < MERGE_END) {
      const t = (p - P1_END) / (MERGE_END - P1_END);
      resetLine(line0); resetLine(line1);
      setLineText(line0, withCursor(combined, false, cursorOn));
      gsap.set(line0, { zIndex: 9999, position: 'relative', opacity: 1 });
      setLineText(line1, letter1);
      gsap.set(line1, { zIndex: 9999, position: 'relative', opacity: 1 - t });
      destroyFly(); phase3Rect = null;
      headerSlot.dataset.morphed = 'false';
      return;
    }

    // ── Phase 2: delete EMANUELP → EP ─────────────────────────────────────
    if (p < P2_END) {
      const txt = p2Text(combined, (p - MERGE_END) / (P2_END - MERGE_END));
      resetLine(line0);
      setLineText(line0, withCursor(txt, false, cursorOn));
      gsap.set(line0, { zIndex: 9999, position: 'relative', opacity: 1 });
      gsap.set(line1, { opacity: 0 });
      destroyFly(); phase3Rect = null;
      headerSlot.dataset.morphed = 'false';
      return;
    }

    // ── Phase 3: fly EP to header ─────────────────────────────────────────
    // Capture rects ONCE on entry (without resetting line0 to avoid jump).
    if (!phase3Rect) {
      setLineText(line0, letter0 + letter1);
      phase3Rect = line0.getBoundingClientRect();
      spawnFly(phase3Rect);
    }

    // Hide in-DOM name lines; fly element is the visual representation
    gsap.set(line0, { opacity: 0 });
    gsap.set(line1, { opacity: 0 });

    if (!flyEl) return;

    const t    = gsap.utils.clamp(0, 1, (p - P2_END) / (1 - P2_END));

    // ── EP fly ─────────────────────────────────────────────────────────────
    const slot = headerSlot.getBoundingClientRect();
    const tgtH = Math.max(slot.height, 28);
    const sc   = gsap.utils.interpolate(1, tgtH / phase3Rect.height, t);
    const x    = gsap.utils.interpolate(phase3Rect.left, slot.left,  t);
    const y    = gsap.utils.interpolate(phase3Rect.top,  slot.top,   t);

    flyEl.style.transform = `translate(${x}px, ${y}px) scale(${sc})`;

    headerSlot.dataset.morphed = t >= 0.99 ? 'true' : 'false';
  }

  // ── Cursor blink (toggles + redraws every 530 ms) ─────────────────────
  const blinkId = setInterval(() => {
    cursorOn = !cursorOn;
    apply(lastP);
  }, 530);

  // ── ScrollTrigger ─────────────────────────────────────────────────────
  const ctx = gsap.context(() => {
    ScrollTrigger.create({
      trigger:            heroSection,
      start:              'top top',
      end:                () => `+=${Math.round(window.innerHeight * SCROLL_VH)}`,
      pin:                true,
      pinSpacing:         true,
      scrub:              0.4,
      anticipatePin:      1,
      invalidateOnRefresh: true,
      refreshPriority:    1,   // calcolato prima di Experience (priorità 0)
      onRefresh: () => {
        startRects = captureNaturalRects();
        destroyFly();
        phase3Rect = null;
      },
      onUpdate: (self) => apply(self.progress),
    });
  }, heroSection);

  apply(0);

  const onResize = () => ScrollTrigger.refresh();
  window.addEventListener('resize', onResize);

  return () => {
    clearInterval(blinkId);
    window.removeEventListener('resize', onResize);
    destroyFly();
    resetLine(line0);
    resetLine(line1);
    setLineText(line0, fullName0);
    setLineText(line1, fullName1);
    if (nameContainer) nameContainer.style.minHeight = '';
    headerSlot.dataset.morphed = 'false';
    ctx.revert();
  };
}
